import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { GELL_PLATES, DORPFELD_ITEMS } from "../data/evidenceData";

// Μεγέθυνση εικόνας σε πλήρη οθόνη
const Lightbox = ({ src, title, caption, closeLabel, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return (
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-anthra/95 p-4 backdrop-blur-sm"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label={closeLabel}
        title={closeLabel}
        className="absolute right-4 top-4 rounded-full border border-paper/40 p-2.5 text-paper transition-colors hover:border-terra hover:text-terra"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt={title}
        data-testid="lightbox-image"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[82vh] max-w-full object-contain shadow-2xl"
      />
      <div className="mt-4 max-w-3xl text-center" onClick={(e) => e.stopPropagation()}>
        <p className="font-serif text-lg font-semibold text-paper">{title}</p>
        <p className="mt-1 text-sm text-paper/70">{caption}</p>
      </div>
    </div>
  );
};

const FigureGrid = ({ items, prefix, folder, lang, onOpen }) => (
  <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((a) => (
      <button
        key={a.img}
        type="button"
        data-testid={`${prefix}-${a.img}`}
        onClick={() => onOpen(a)}
        className="group overflow-hidden rounded-xl border border-line bg-paper text-left transition-transform duration-200 hover:-translate-y-1"
      >
        <img src={`/images/${folder}/${a.img}.jpg`} alt={a[lang].title} loading="lazy" className="h-auto w-full" />
        <div className="p-4">
          <p className="font-serif text-lg font-semibold text-ink group-hover:text-terra">{a[lang].title}</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{a[lang].caption}</p>
        </div>
      </button>
    ))}
  </div>
);

// «Ιστορικά Τεκμήρια της αναζήτησης» — χαλκογραφίες Gell, φωτογραφίες Dörpfeld
export const EvidencePage = () => {
  const { lang, ui } = useLang();
  const [zoom, setZoom] = useState(null);
  const openItem = (folder) => (a) => setZoom({ ...a, src: `/images/${folder}/${a.img}.jpg` });

  return (
    <main data-testid="evidence-main" className="min-h-[70vh] bg-surface/50">
      <div className="mx-auto max-w-6xl px-6 pb-14 pt-10">
        <p className="eyebrow">{ui.evEyebrow}</p>
        <h1 data-testid="evidence-title" className="mt-2 font-serif text-3xl font-semibold tracking-tight text-ink md:text-5xl">
          {ui.heroOptArt}
        </h1>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">{ui.evIntro}</p>

        <section data-testid="gell-section" className="mt-16 border-t border-line pt-12">
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink md:text-4xl">{ui.evGellTitle}</h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">{ui.evGellIntro}</p>
          <FigureGrid items={GELL_PLATES} prefix="gell-card" folder="tekmiiria" lang={lang} onOpen={openItem("tekmiiria")} />
        </section>

        <section data-testid="dorpfeld-section" className="mt-16 border-t border-line pt-12">
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink md:text-4xl">{ui.evDorpfeldTitle}</h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">{ui.evDorpfeldIntro}</p>
          <FigureGrid items={DORPFELD_ITEMS} prefix="dorpfeld-card" folder="tekmiiria" lang={lang} onOpen={openItem("tekmiiria")} />
        </section>

      </div>

      {zoom && (
        <Lightbox
          src={zoom.src}
          title={zoom[lang].title}
          caption={zoom[lang].caption}
          closeLabel={ui.lightboxClose}
          onClose={() => setZoom(null)}
        />
      )}
    </main>
  );
};
